// The browser voice: the Justices voiced by a model running in this page.
//
// When the server serves it (GET /api/browser-voice, BROWSER_VOICE_ENABLED), a
// capable device loads the voice-distill project's runtime from /tts/ in the
// background: two Web Workers and onnxruntime-web, the r6 model (about 110 MB,
// kept in the Cache API after the first visit). Once it has loaded, warmed up
// and measured itself faster than real time, the game socket's hello says so
// (ws.ts), and a session past the cloned-voice cap gets its Justices as text
// frames to voice here (audio.ts, 'local' mode) instead of the server's
// standard voices. Nothing here runs unless the server turned it on.
//
// It loads only for a session that will use it: when a page outside a game
// opens while the cloned voices are all taken (the config's `preload`), or
// when a game is put on the standard voices (voice.tier, ws.ts calls
// wantBrowserVoice).
// The load is a 110 MB download on a first visit and several seconds of
// every core on each page load, and on a page that loaded it for nothing it
// competed with the game it was not needed for: the first staging game with
// it on (2026-10-16, a cloned-voice session) had the download running under
// the Marshal's opening.
//
// Quality and latency, as measured on the demo and in the game:
// - Each Justice is played at the loudness the model's other clips are judged
//   at (-24 LUFS, from the per-voice table voice_lufs.json), with the codec
//   correction the model set asks for (the runtime applies it per chunk).
// - The model left about 0.3 s of silence before a piece's first sound (p50,
//   up to 0.54 s; bin/browservoicebench.mjs, nine voices), which the runtime
//   now cuts to 80 ms itself (voice-distill a2c7519), and 0.1 to 0.3 s after
//   its last, which is cut here (SeamGate). The pause between pieces is set
//   by how the piece ends, to what r6 leaves inside one take: 0.36 s at a
//   sentence break, 0.12 s at a clause. Every seam sounds like one take
//   instead of a double pause.
// - The first chunk of a piece is decoded about 60 ms after it is asked for
//   on a desktop. A short piece that passed the runtime's take check is kept
//   (up to three takes per voice and text) and replayed at random.
// - The sitting Court's voices are prepared (their prompts run through the
//   model) right after loading, so the first line in each voice starts at once.

/** What the server says about the browser voice. */
interface BrowserVoiceConfig {
  enabled: boolean;
  model: string | null;
  base: string | null;
  // The cloned voices are all taken right now: a game started from this
  // page would get the standard voices, so load before it starts.
  preload?: boolean;
}

/** The part of the runtime's BrowserVoice class this module uses. */
interface RuntimeVoice {
  config: { sample_rate: number };
  stream(text: string, voice: string, seed: number, onChunk: (s: Float32Array) => void): Promise<{ rtf: number | null; audioS: number; url?: string; ok?: boolean }>;
  prepare(voice: string): Promise<unknown>;
  terminate(): void;
  // Set by the runtime once its workers are gone (a crash or terminate()).
  dead?: boolean;
}
interface RuntimeModule {
  probe(): Promise<{ simd: boolean; isolated: boolean; cores: number }>;
  BrowserVoice: { load(opts: Record<string, unknown>): Promise<RuntimeVoice> };
}

/** Fastest a device may be to get the browser voice: 1.6x real time. */
export const MAX_RTF = 0.62;
/** A piece this short is kept and replayed (the address a reply opens with). */
export const SHORT_PIECE_CHARS = 24;
// Up to three good takes are kept; replayed once there are two.
const SHORT_PIECE_TAKES = 3;
const REPLAY_AFTER = 2;
// Distinct short pieces kept (about 50 KB a take), oldest dropped first.
const MAX_KEPT_PIECES = 64;
// The tail trim, in 20 ms frames below the server's threshold for its own
// pieces (src/api/ws_turn.py, _SEAM_SILENCE_DBFS); 60 ms is kept so a decay
// is never clipped. The head is the runtime's: it starts a take 80 ms before
// its first frame above -50 dBFS (voice-distill a2c7519).
const SILENCE_DBFS = -45;
export const HEAD_KEEP_S = 0.08;
export const TAIL_KEEP_S = 0.06;
// The whole pause between two pieces of a line, by how the first one ends.
const SENTENCE_PAUSE_S = 0.36;
const CLAUSE_PAUSE_S = 0.12;
const TARGET_LUFS = -24;
const DEFAULT_LUFS = -21.8;
export const SITTING_COURT = ['roberts', 'thomas', 'alito', 'sotomayor', 'kagan', 'gorsuch', 'kavanaugh', 'barrett', 'jackson'];

type Phase = 'off' | 'loading' | 'ready' | 'unfit' | 'failed';

let phase: Phase = 'off';

// The phase on <html data-browser-voice>, for probes and diagnostics only.
function setPhase(next: Phase): void {
  phase = next;
  try { document.documentElement.dataset.browserVoice = next; } catch { /* no DOM */ }
}
let model: string | null = null;
let voice: RuntimeVoice | null = null;
let normalize: (t: string) => string = (t) => t;
let lufs: Record<string, number> = {};
let measuredRtf: number | null = null;
let started: Promise<void> | null = null;
let configP: Promise<BrowserVoiceConfig | null> | null = null;
let inGame = false;
const takes = new Map<string, Float32Array[][]>();

const changeListeners = new Set<(model: string | null) => void>();

/**
 * Call ``cb`` with the model when the voice becomes ready, and with null if it
 * is lost later (its workers died). Returns the unsubscribe function.
 */
export function onBrowserVoiceChange(cb: (model: string | null) => void): () => void {
  changeListeners.add(cb);
  return () => { changeListeners.delete(cb); };
}

function announce(m: string | null): void {
  for (const cb of [...changeListeners]) { try { cb(m); } catch { /* a listener's problem */ } }
}

// The workers are gone (a phone under memory pressure kills them): nothing
// more can be voiced here, so say so and the server voices the rest.
function lose(v: RuntimeVoice): void {
  if (voice !== v) return;
  voice = null;
  takes.clear();
  setPhase('failed');
  try { v.terminate(); } catch { /* already gone */ }
  announce(null);
}

/** The model name to announce in the hello, once the voice is ready; else null. */
export function browserVoiceReady(): string | null {
  return phase === 'ready' ? model : null;
}

/** For tests and diagnostics. */
export function browserVoiceState(): { phase: Phase; model: string | null; rtf: number | null } {
  return { phase, model, rtf: measuredRtf };
}

/** The sample rate of what speak() hands back (24 kHz for r6). */
export function browserVoiceRate(): number {
  return voice ? voice.config.sample_rate : 24000;
}

/** How long a line should bank before it starts, on this device. */
export function browserVoicePrebufferS(): number {
  // A device well ahead of real time can start on the first decoded chunk;
  // one near the line needs a cushion (the demo's rule).
  if (measuredRtf === null || measuredRtf < 0.35) return 0.1;
  return Math.min(0.6, 0.1 + (measuredRtf - 0.35) * 1.5);
}

function capable(): boolean {
  if (typeof window === 'undefined' || typeof Worker === 'undefined') return false;
  // WASM threads need SharedArrayBuffer, which needs cross-origin isolation.
  if (!(window as unknown as { crossOriginIsolated?: boolean }).crossOriginIsolated) return false;
  const cores = navigator.hardwareConcurrency || 0;
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  return cores >= 4 && (mem === undefined || mem >= 4);
}

function fetchConfig(fetchImpl: typeof fetch): Promise<BrowserVoiceConfig | null> {
  if (configP) return configP;
  configP = (async () => {
    try {
      const r = await fetchImpl('/api/browser-voice', { cache: 'no-cache' });
      if (!r.ok) return null;
      const cfg = (await r.json()) as BrowserVoiceConfig;
      return cfg.enabled && cfg.model && cfg.base ? cfg : null;
    } catch {
      return null;
    }
  })();
  return configP;
}

/**
 * At page load: ask the server whether it serves the browser voice, and load
 * it now only if a game started from here would need it (the config's
 * `preload`). Safe to call more than once.
 */
export function initBrowserVoice(fetchImpl: typeof fetch = fetch): Promise<void> {
  return fetchConfig(fetchImpl).then((cfg) => (cfg && cfg.preload && !inGame ? load(cfg, fetchImpl) : undefined));
}

/**
 * A game socket opened on this page. From then on the game's voice.tier
 * decides (wantBrowserVoice): the page-load `preload` counts that game's own
 * session among the cloned voices taken, so on a game page it reads "all
 * taken" for a session that holds one of them.
 */
export function noteGameSession(): void {
  inGame = true;
}

/**
 * This session is on the standard voices: load the browser voice if the
 * server serves it and this device can run it. Once it is ready the game
 * socket says so and the Justices move to it from their next line. Safe to
 * call more than once.
 */
export function wantBrowserVoice(fetchImpl: typeof fetch = fetch): Promise<void> {
  return fetchConfig(fetchImpl).then((cfg) => (cfg ? load(cfg, fetchImpl) : undefined));
}

function load(cfg: BrowserVoiceConfig, fetchImpl: typeof fetch): Promise<void> {
  if (started) return started;
  started = (async () => {
    if (!cfg.model || !cfg.base) return;
    if (!capable()) { setPhase('unfit'); return; }
    setPhase('loading');
    try {
      const base = cfg.base;
      // Outside the bundle on purpose: the runtime lives with its workers.
      const mod = (await import(/* @vite-ignore */ `${base}tts.js`)) as RuntimeModule;
      const norm = (await import(/* @vite-ignore */ `${base}textnorm.js`)) as { normalize: (t: string) => string };
      normalize = norm.normalize;
      lufs = await fetchImpl(`${base}voice_lufs.json`).then((r) => r.json()).catch(() => ({}));
      const p = await mod.probe();
      if (!p.simd || !p.isolated) { setPhase('unfit'); return; }
      voice = await mod.BrowserVoice.load({ model: cfg.model, probe: p });
      // One real line, timed: the hello only offers what this device can keep up with.
      const r = await voice.stream('Counsel, what is the limiting principle of your rule?', 'roberts', 1, () => {});
      if (r.url) URL.revokeObjectURL(r.url);
      measuredRtf = r.rtf;
      try { document.documentElement.dataset.browserVoiceRtf = String(r.rtf); } catch { /* no DOM */ }
      if (r.rtf === null || r.rtf > MAX_RTF) {
        setPhase('unfit');
        voice.terminate();
        voice = null;
        return;
      }
      model = cfg.model;
      setPhase('ready');
      announce(cfg.model);
      for (const j of SITTING_COURT) void voice.prepare(j).catch(() => {});
    } catch (err) {
      console.warn('[browserVoice] load failed', err);
      setPhase('failed');
    }
  })();
  return started;
}

// Timings for probes (bin/turnprobe.mjs sets window.__bvTrace to an array).
function trace(ev: string): void {
  try {
    const t = (window as unknown as { __bvTrace?: Array<{ t: number; ev: string }> }).__bvTrace;
    if (t) t.push({ t: performance.now(), ev });
  } catch { /* no window */ }
}

function gainFor(j: string): number {
  return Math.pow(10, (TARGET_LUFS - (lufs[j] ?? DEFAULT_LUFS)) / 20);
}

// Always a copy: kept takes must not share a buffer with the runtime.
function scaled(chunk: Float32Array, g: number): Float32Array {
  if (g === 1) return chunk.slice();
  const out = new Float32Array(chunk.length);
  for (let i = 0; i < chunk.length; i++) out[i] = Math.max(-1, Math.min(1, chunk[i] * g));
  return out;
}

/**
 * The silence to add between this piece and the next one of the same line,
 * on top of the edges SeamGate keeps.
 */
export function pauseAfter(text: string): number {
  const whole = /[.?!]["'\u201d)\]]*$/.test(text.trimEnd()) ? SENTENCE_PAUSE_S : CLAUSE_PAUSE_S;
  return Math.max(0, whole - HEAD_KEEP_S - TAIL_KEEP_S);
}

/** How long a piece may take before the runtime counts as hung. */
export function stallLimitMs(text: string): number {
  // About 0.4 s of speech a word, rendered at the slowest admitted speed
  // (MAX_RTF) is 0.25 s a word; this allows more than twice that, plus the
  // first render's warm-up.
  return 8000 + 600 * text.trim().split(/\s+/).length;
}

/**
 * Holds back the silence after a streamed piece's latest sound until more
 * sound follows (a pause inside the piece, passed on whole) or the piece ends
 * (cut to TAIL_KEEP_S). Nothing before the first sound is held: the runtime
 * cuts a take's lead itself, to HEAD_KEEP_S, and whatever it passes is meant
 * to be heard. Only silence is ever held, so no sound is delayed.
 */
export class SeamGate {
  private readonly win: number;
  private readonly floor: number;
  private heard = false;
  private tail: Float32Array[] = [];

  constructor(private readonly rate: number, private readonly emit: (s: Float32Array) => void) {
    this.win = Math.max(1, Math.round(rate * 0.02));
    this.floor = Math.pow(10, SILENCE_DBFS / 20);
  }

  private loud(x: Float32Array, from: number, to: number): boolean {
    let e = 0;
    for (let i = from; i < to; i++) e += x[i] * x[i];
    return Math.sqrt(e / Math.max(1, to - from)) >= this.floor;
  }

  push(x: Float32Array): void {
    // End of the last loud frame in x (frames counted from x's start).
    let cut = 0;
    for (let i = 0; i < x.length; i += this.win) {
      const to = Math.min(x.length, i + this.win);
      if (this.loud(x, i, to)) cut = to;
    }
    if (cut === 0) {
      if (this.heard) this.tail.push(x);
      else this.emit(x);
      return;
    }
    this.heard = true;
    for (const t of this.tail) this.emit(t);
    this.tail = [];
    this.emit(x.subarray(0, cut));
    if (cut < x.length) this.tail.push(x.subarray(cut));
  }

  /** The piece is over: let out TAIL_KEEP_S of what was held, drop the rest. */
  end(): void {
    let keep = Math.round(TAIL_KEEP_S * this.rate);
    for (const t of this.tail) {
      if (keep <= 0) break;
      const part = t.length <= keep ? t : t.subarray(0, keep);
      this.emit(part);
      keep -= part.length;
    }
    this.tail = [];
  }
}

/**
 * Voice one piece of a Justice's speech, handing each audio chunk to
 * ``onChunk`` as it is decoded (mono, ``browserVoiceRate()``), cut to its
 * sound (SeamGate). Pieces are voiced one at a time, in the order they are
 * asked for. Resolves with the pause owed before the line's next piece (0 when
 * nothing played); never rejects (a failed piece is silence).
 */
export async function speak(j: string, text: string, onChunk: (s: Float32Array) => void): Promise<number> {
  const v = voice;
  if (!v) return 0;
  const said = normalize(text).trim();
  if (!said) return 0;
  const pause = pauseAfter(said);
  const g = gainFor(j);
  const key = said.length <= SHORT_PIECE_CHARS ? `${j}\u0000${said}` : null;
  const kept = key ? takes.get(key) : undefined;
  if (key && kept && kept.length >= REPLAY_AFTER) {
    for (const c of kept[Math.floor(Math.random() * kept.length)]) onChunk(c);
    return pause;
  }
  const chunks: Float32Array[] = [];
  const gate = new SeamGate(v.config.sample_rate, (c) => {
    if (!chunks.length) trace('emit0');
    chunks.push(c);
    onChunk(c);
  });
  let ok = false;
  trace('speak');
  let raw = 0;
  let stalled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // The runtime also hands back the whole clip as an object URL (the demo
    // plays it); the game only wants the chunks, so free it at once.
    const run = v.stream(said, j, Math.floor(Math.random() * 2 ** 31), (s) => {
      if (stalled) return;
      trace(`chunk${raw++}:${s.length}`);
      gate.push(scaled(s, g));
    });
    // A piece that runs far past what the slowest admitted device needs has
    // hung the runtime, and every later piece would queue behind it: give the
    // voice up so the server voices the rest.
    const stall = new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('piece stalled')), stallLimitMs(said));
    });
    const r = await Promise.race([run, stall]);
    if (r.url) URL.revokeObjectURL(r.url);
    // The runtime's take check (ended on its own, a plausible speaking rate,
    // no long dead air): only a good take is kept for replay.
    ok = r.ok !== false;
  } catch (err) {
    console.warn('[browserVoice] piece failed', err);
    if (err instanceof Error && err.message === 'piece stalled') {
      stalled = true;
      lose(v);
    } else if (v.dead) {
      lose(v);
    }
  } finally {
    clearTimeout(timer);
  }
  gate.end();
  if (!chunks.length) return 0;
  if (key && ok) {
    const list = takes.get(key) ?? [];
    if (list.length < SHORT_PIECE_TAKES) list.push(chunks);
    takes.set(key, list);
    if (takes.size > MAX_KEPT_PIECES) takes.delete(takes.keys().next().value as string);
  }
  return pause;
}

/** Test seam: tell the listeners the voice became ready (a model) or was lost (null). */
export function _announceForTest(m: string | null): void {
  announce(m);
}

/** Test seam: install a runtime without loading one. */
export function _installForTest(v: RuntimeVoice | null, opts: { model?: string; rtf?: number; lufs?: Record<string, number>; normalize?: (t: string) => string } = {}): void {
  voice = v;
  model = opts.model ?? 'r6';
  measuredRtf = opts.rtf ?? 0.2;
  lufs = opts.lufs ?? {};
  normalize = opts.normalize ?? ((t) => t);
  phase = v ? 'ready' : 'off';
  takes.clear();
  changeListeners.clear();
  started = null;
  configP = null;
  inGame = false;
}
